import axios from "axios";
import { deleteAsync, getAsync, postAsync, putAsync, TEAM_ID_HEADER } from "../protect-api";
import config from "../../config";
import { apiVersion, UserStatus } from "../../shared/constants/common.constant";
import type { ActiveUser as ComplianceActiveUser } from "../../store/UserSlice";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "react-query";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type {
  CreateTeamPayload,
  CreateTeamResponse,
  DeleteTeamPayload,
  GetActiveUsersResponse,
  GetTeamMembersResponse,
  GetTeamsResponse,
  RemoveTeamMembersPayload,
  RemoveTeamMembersFailedItem,
  RemoveTeamMembersResponse,
  Team,
  TeamMutationRequest,
  TeamMutationResponse,
  TeamMember,
  TeamMemberApiResponse,
  TeamPreferenceResponse,
  TeamStatus,
  UpdatePreferredTeamPayload,
  UpdatePreferredTeamResponse,
  UpdateTeamPayload,
  UpdateTeamResponse,
  AddTeamMembersPayload,
  AddTeamMembersResponse,
  ActiveUser,
  ActiveTeamAccessResult,
  TeamDetailsResponse,
} from "./teams.types";
import { TEAM_STATUS } from "./teams.types";
import { resolveAccessLostKindFromError } from "./active-team-access";
import {
  getActiveTeamId,
  setLastKnownActiveTeamName,
} from "../../utils/active-team/active-team";
import type { Paginator } from "../../shared/types/table.type";
import type { User } from "../../shared/types/user.type";
import { createQueryString } from "../../utils";

export const TEAMS_KEY = "teams";
export const ACTIVE_TEAM_ACCESS_KEY = "active-team-access";
export const ACTIVE_TEAM_ACCESS_POLL_MS = 15_000;
export const TEAM_MEMBERS_KEY = "team-members";
export const ALL_TEAM_MEMBERS_KEY = "all-team-members";
export const ALL_TEAM_MEMBERS_FETCH_LIMIT = 100;
export const TEAM_PREFERENCE_KEY = "team-preference";
export const ACTIVE_USERS_KEY = "active-users";

export const updateTeamDeletionStatusInCache = (
  queryClient: QueryClient,
  teamId: string,
  status: TeamStatus,
  profilesCount?: number
): void => {
  queryClient
    .getQueryCache()
    .findAll([TEAMS_KEY])
    .forEach((query) => {
      queryClient.setQueryData<GetTeamsResponse | undefined>(
        query.queryKey,
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            teams: oldData.teams.map((team) => {
              if (team.id !== teamId) {
                return team;
              }

              const isDataMovement = status === TEAM_STATUS.MovingData;

              return {
                ...team,
                status,
                isDeletionInProgress: true,
                isDataMovementInProgress: isDataMovement,
                ...(isDataMovement && profilesCount !== undefined
                  ? { profilesCount }
                  : {}),
              };
            }),
          };
        }
      );
    });
};

const normalizeTeamStatus = (status: unknown): TeamStatus | undefined => {
  if (typeof status !== "string") {
    return undefined;
  }

  const normalized = status.trim().toLowerCase();

  if (normalized === "deleting" || normalized === "deleted") {
    return TEAM_STATUS.Deleting;
  }

  if (
    normalized === "movingdata" ||
    normalized === "moving_data" ||
    normalized === "datamoved" ||
    normalized === "data_moved"
  ) {
    return TEAM_STATUS.MovingData;
  }

  if (normalized === "active") {
    return TEAM_STATUS.Active;
  }

  return undefined;
};

const mapTeamFromApi = (
  team: Team & { profileCount?: number; profiles_count?: number }
): Team => {
  const status = normalizeTeamStatus(team.status) ?? team.status;
  const profilesCount =
    team.profilesCount ?? team.profileCount ?? team.profiles_count;
  const isDataMovementInProgress =
    team.isDataMovementInProgress === true ||
    status === TEAM_STATUS.MovingData;
  const isDeletionInProgress =
    team.isDeletionInProgress === true ||
    isDataMovementInProgress ||
    status === TEAM_STATUS.Deleting;

  return {
    ...team,
    status,
    profilesCount,
    isDeletionInProgress,
    isDataMovementInProgress,
  };
};

const buildTeamHeaders = (teamId: string): Record<string, string> => ({
  [TEAM_ID_HEADER]: teamId,
});

const buildTeamsUrl = (path = ""): string =>
  `${config.KYCP_AUTH_API_URL}/${apiVersion}/teams${path}`;

const toTeamMutationRequest = (
  teamName: string,
  description: string
): TeamMutationRequest => ({
  name: teamName,
  description,
});

const mapTeamMutationResponse = (
  data: TeamMutationResponse | undefined,
  fallback: TeamMutationResponse
): CreateTeamResponse => ({
  id: data?.id ?? fallback.id,
  teamName: data?.name ?? fallback.name,
  description: data?.description ?? fallback.description,
});

const createTeam = async (
  payload: CreateTeamPayload
): Promise<CreateTeamResponse> => {
  const request = toTeamMutationRequest(payload.teamName, payload.description);
  const response = await postAsync<TeamMutationResponse>(
    buildTeamsUrl(),
    request
  );
  return mapTeamMutationResponse(response.data, {
    id: "",
    name: payload.teamName,
    description: payload.description,
  });
};

export const useCreateTeam = (): UseMutationResult<
  CreateTeamResponse,
  ProblemDetails,
  CreateTeamPayload
> =>
  useMutation<CreateTeamResponse, ProblemDetails, CreateTeamPayload>(
    createTeam
  );

const updateTeam = async (
  payload: UpdateTeamPayload
): Promise<UpdateTeamResponse> => {
  const { teamId, teamName, description } = payload;
  const request = toTeamMutationRequest(teamName, description);
  const response = await putAsync<TeamMutationResponse>(
    buildTeamsUrl(`/${teamId}`),
    request,
    buildTeamHeaders(teamId)
  );
  return mapTeamMutationResponse(response.data, {
    id: teamId,
    name: teamName,
    description,
  });
};

export const useUpdateTeam = (): UseMutationResult<
  UpdateTeamResponse,
  ProblemDetails,
  UpdateTeamPayload
> =>
  useMutation<UpdateTeamResponse, ProblemDetails, UpdateTeamPayload>(
    updateTeam
  );

const getTeams = async (query: Paginator): Promise<GetTeamsResponse> => {
  try {
    const { data } = await getAsync(
      `${config.KYCP_AUTH_API_URL}/${apiVersion}/teams?${createQueryString(
        query
      )}`,
      { [TEAM_ID_HEADER]: "" }
    );
    return {
      count: Number.parseInt(data?.count ?? data?.totalSize, 10),
      teams: (data.items as Team[]).map(mapTeamFromApi),
      apiFailed: false,
    };
  } catch {
    return {
      count: 0,
      teams: [],
      apiFailed: true,
    };
  }
};

export const useGetTeams = (
  query: Paginator,
  options?: { enabled?: boolean; refetchInterval?: number | false }
): ReturnType<typeof useQuery<GetTeamsResponse, ProblemDetails>> => {
  return useQuery<GetTeamsResponse, ProblemDetails>(
    [TEAMS_KEY, query],
    () => getTeams(query),
    {
      enabled: options?.enabled ?? true,
      refetchInterval: options?.refetchInterval ?? false,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
};

export const getActiveTeamAccess = async (
  teamId: string
): Promise<ActiveTeamAccessResult> => {
  try {
    const { data } = await getAsync<TeamDetailsResponse>(
      buildTeamsUrl(`/${teamId}`)
    );
    return { kind: "ok", teamName: data?.name ?? "" };
  } catch (error) {
    const accessLostKind = resolveAccessLostKindFromError(error);
    if (accessLostKind) {
      return { kind: accessLostKind };
    }
    throw error;
  }
};

export const usePollActiveTeamAccess = (
  teamId: string,
  options?: { enabled?: boolean; pollIntervalMs?: number | false }
): UseQueryResult<ActiveTeamAccessResult, ProblemDetails> => {
  const pollIntervalMs =
    options?.pollIntervalMs ??
    (process.env.NODE_ENV === "test" ? false : ACTIVE_TEAM_ACCESS_POLL_MS);

  return useQuery<ActiveTeamAccessResult, ProblemDetails>(
    [ACTIVE_TEAM_ACCESS_KEY, teamId],
    () => getActiveTeamAccess(teamId),
    {
      enabled: (options?.enabled ?? true) && Boolean(teamId),
      refetchInterval: (data) => {
        if (pollIntervalMs === false) {
          return false;
        }
        return data && data.kind !== "ok" ? false : pollIntervalMs;
      },
      refetchIntervalInBackground: true,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (result) => {
        if (result.kind === "ok" && result.teamName) {
          setLastKnownActiveTeamName(result.teamName, teamId);
        }
      },
    }
  );
};

const deleteTeam = async (payload: DeleteTeamPayload): Promise<void> => {
  const { teamId, permissionTeamId, actionOnRecords } = payload;
  const headerTeamId = permissionTeamId?.trim();
  await deleteAsync(
    buildTeamsUrl(`/${teamId}`),
    { actionOnRecords },
    headerTeamId ? buildTeamHeaders(headerTeamId) : undefined
  );
};

export const useDeleteTeam = (): UseMutationResult<
  void,
  ProblemDetails,
  DeleteTeamPayload
> => useMutation<void, ProblemDetails, DeleteTeamPayload>(deleteTeam);

const updatePreferredTeam = async (
  payload: UpdatePreferredTeamPayload
): Promise<UpdatePreferredTeamResponse> => {
  const response = await putAsync<UpdatePreferredTeamResponse>(
    `${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`,
    payload
  );
  return response.data;
};

export const useUpdatePreferredTeam = (): UseMutationResult<
  UpdatePreferredTeamResponse,
  ProblemDetails,
  UpdatePreferredTeamPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdatePreferredTeamResponse,
    ProblemDetails,
    UpdatePreferredTeamPayload
  >(updatePreferredTeam, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<TeamPreferenceResponse>([TEAM_PREFERENCE_KEY], {
        teamId: data.teamId || variables.teamId,
        teamName: data.teamName,
      });
    },
  });
};

const isHttpNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const getTeamPreference = async (): Promise<TeamPreferenceResponse | null> => {
  try {
    const { data } = await getAsync<TeamPreferenceResponse>(
      `${config.KYCP_AUTH_API_URL}/${apiVersion}/preferences/team`
    );
    return data;
  } catch (error) {
    if (isHttpNotFoundError(error)) {
      return null;
    }

    throw error;
  }
};

export const useGetTeamPreference = (options?: {
  enabled?: boolean;
}): UseQueryResult<TeamPreferenceResponse | null, ProblemDetails> =>
  useQuery<TeamPreferenceResponse | null, ProblemDetails>(
    [TEAM_PREFERENCE_KEY],
    () => getTeamPreference(),
    {
      enabled: options?.enabled ?? true,
    }
  );

const mapComplianceUserToTeamActiveUser = (
  user: ComplianceActiveUser
): ActiveUser => {
  const firstName = user.firstName ?? "";
  const lastName = user.surname ?? "";
  const fullName =
    [firstName, lastName].filter(Boolean).join(" ") || user.username || "";

  return {
    userId: user.userId,
    firstName,
    lastName,
    fullName,
    email: user.email,
    status: user.isActive ? UserStatus.Active : UserStatus.Inactive,
  };
};

const getActiveUsers = async (): Promise<GetActiveUsersResponse> => {
  const { data } = await getAsync<ComplianceActiveUser[]>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/users/active`,
    undefined,
    undefined,
    true
  );
  const items = data.map(mapComplianceUserToTeamActiveUser);

  return { items, count: items.length };
};

export const useGetActiveUsers = (
  enabled = true
): UseQueryResult<GetActiveUsersResponse, ProblemDetails> =>
  useQuery<GetActiveUsersResponse, ProblemDetails>(
    [ACTIVE_USERS_KEY],
    getActiveUsers,
    {
      enabled,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

const addTeamMembers = async (
  teamId: string,
  payload: AddTeamMembersPayload
): Promise<AddTeamMembersResponse> => {
  const { data } = await postAsync<AddTeamMembersResponse>(
    buildTeamsUrl(`/${teamId}/users`),
    payload,
    buildTeamHeaders(teamId)
  );

  return {
    successful: data?.successful ?? [],
    failed: data?.failed ?? [],
  };
};

export const useAddTeamMembers = (): UseMutationResult<
  AddTeamMembersResponse,
  ProblemDetails,
  { teamId: string; payload: AddTeamMembersPayload }
> => {
  const queryClient = useQueryClient();
  return useMutation<
    AddTeamMembersResponse,
    ProblemDetails,
    { teamId: string; payload: AddTeamMembersPayload }
  >(({ teamId, payload }) => addTeamMembers(teamId, payload), {
    onSuccess: (data, variables) => {
      if (data.successful.length === 0) {
        return;
      }
      void queryClient.invalidateQueries([TEAMS_KEY]);
      void queryClient.invalidateQueries([TEAM_MEMBERS_KEY, variables.teamId]);
    },
  });
};

const splitDisplayName = (
  displayName: string
): { firstName: string; lastName: string } => {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const mapTeamMemberFromApi = (
  member: Partial<TeamMember> &
    Partial<TeamMemberApiResponse> & { surname?: string; username?: string }
): TeamMember => {
  const displayName =
    (typeof member.fullName === "string" ? member.fullName.trim() : "") ||
    (typeof member.userName === "string" ? member.userName.trim() : "") ||
    (typeof member.username === "string" ? member.username.trim() : "") ||
    "";
  const { firstName: splitFirstName, lastName: splitLastName } =
    splitDisplayName(displayName);
  const firstName = member.firstName ?? splitFirstName;
  const lastName = member.lastName ?? member.surname ?? splitLastName;

  return {
    userId: member.userId ?? 0,
    firstName,
    lastName,
    fullName: displayName || [firstName, lastName].filter(Boolean).join(" "),
    email: member.email ?? "",
    roleId: member.roleId ?? 0,
    roleName: member.roleName ?? "",
  };
};

const getTeamMembers = async (
  teamId: string,
  page: number,
  limit: number
): Promise<GetTeamMembersResponse> => {
  const response = await getAsync<{
    items: TeamMemberApiResponse[];
    count: number;
  }>(
    `${config.KYCP_AUTH_API_URL}/${apiVersion}/teams/${teamId}/users?page=${page}&limit=${limit}`
  );
  const data = response.data;

  return {
    items: Array.isArray(data?.items)
      ? data.items.map((member) => mapTeamMemberFromApi(member))
      : [],
    count: data?.count ?? 0,
  };
};

export const useGetTeamMembers = (
  teamId: string,
  page: number,
  limit: number,
  enabled = true,
  keepPreviousData = true
): ReturnType<typeof useQuery<GetTeamMembersResponse>> =>
  useQuery<GetTeamMembersResponse>(
    [TEAM_MEMBERS_KEY, teamId, page, limit],
    () => getTeamMembers(teamId, page, limit),
    {
      enabled: enabled && Boolean(teamId),
      keepPreviousData,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

const mapTeamMemberToUser = (member: TeamMember): User => ({
  userId: member.userId,
  username: member.fullName,
  email: member.email,
  firstName: member.firstName,
  surname: member.lastName,
  isActive: true,
});

const getAllTeamMembers = async (teamId: string): Promise<User[]> => {
  const members: TeamMember[] = [];
  let page = 1;
  let totalCount = 0;

  do {
    const response = await getTeamMembers(
      teamId,
      page,
      ALL_TEAM_MEMBERS_FETCH_LIMIT
    );

    if (response.items.length === 0) {
      break;
    }

    totalCount = response.count;
    members.push(...response.items);
    page += 1;
  } while (members.length < totalCount);

  return members.map(mapTeamMemberToUser);
};

export const useGetAllTeamMembers = (
  enabled = true
): UseQueryResult<User[], ProblemDetails> => {
  const teamId = getActiveTeamId() ?? "";

  return useQuery<User[], ProblemDetails>(
    [ALL_TEAM_MEMBERS_KEY, teamId],
    () => getAllTeamMembers(teamId),
    {
      enabled: enabled && Boolean(teamId),
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};

const toProblemDetailsFromRemoveFailure = (
  failure: RemoveTeamMembersFailedItem
): ProblemDetails => {
  const error = failure.error;

  return {
    status: error?.status,
    type: error?.type,
    title: error?.title,
    detail: error?.detail,
    details:
      error?.details ??
      (typeof error?.detail === "string"
        ? {
            type: error.type,
            title: error.title,
            status: error.status,
            detail: error.detail,
          }
        : undefined),
  };
};

const assertRemoveTeamMembersSucceeded = (
  response: RemoveTeamMembersResponse
): void => {
  if (response.failed.length > 0) {
    throw toProblemDetailsFromRemoveFailure(response.failed[0]);
  }
};

const removeTeamMembers = async (
  teamId: string,
  payload: RemoveTeamMembersPayload
): Promise<void> => {
  const { data } = await deleteAsync<RemoveTeamMembersResponse>(
    buildTeamsUrl(`/${teamId}/users`),
    payload,
    buildTeamHeaders(teamId)
  );

  assertRemoveTeamMembersSucceeded({
    successful: data?.successful ?? [],
    failed: data?.failed ?? [],
  });
};

export const useRemoveTeamMembers = (): UseMutationResult<
  void,
  ProblemDetails,
  { teamId: string; payload: RemoveTeamMembersPayload }
> => {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    ProblemDetails,
    { teamId: string; payload: RemoveTeamMembersPayload }
  >(({ teamId, payload }) => removeTeamMembers(teamId, payload), {
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries([TEAMS_KEY]);
      void queryClient.invalidateQueries([TEAM_MEMBERS_KEY, variables.teamId]);
    },
  });
};
